import { useState, useEffect } from "react";
import { getImageUrl } from "@/lib/imageUtils";
import { clearBrandingCache } from "@/lib/pdfBranding";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Save, Building2, FileText, Mail, Phone, CreditCard, Palette, Download, FolderOpen, CalendarDays, Plug, FileSignature } from "lucide-react";
import { HoldedIntegracionSection } from "@/components/configuracion/HoldedIntegracionSection";
import { HoldedAutomatizacionSection } from "@/components/configuracion/HoldedAutomatizacionSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlantillasDocumentosManager } from "@/components/configuracion/PlantillasDocumentosManager";
import { PlantillasContratosManager } from "@/components/configuracion/PlantillasContratosManager";
import { PlantillasCertificadosManager } from "@/components/configuracion/PlantillasCertificadosManager";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { LogoUpload } from "@/components/empresa/LogoUpload";
import { SelloUpload } from "@/components/empresa/SelloUpload";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PdfDocumentBuilder, type EmpresaInfo } from "@/lib/pdfDocumentEngine";

// Validación de NIF/CIF español
const validarNifCif = (value: string) => {
  if (!value) return true;
  const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
  const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/i;
  return nifRegex.test(value) || cifRegex.test(value);
};

const empresaFormSchema = z.object({
  nombre: z.string().min(2, "El nombre debe tener al menos 2 caracteres").max(100, "Máximo 100 caracteres"),
  razon_social: z.string().max(200, "Máximo 200 caracteres").optional().nullable(),
  nif_cif: z.string()
    .optional()
    .nullable()
    .refine((val) => !val || validarNifCif(val), {
      message: "Formato de NIF/CIF inválido"
    }),
  direccion_calle: z.string().max(200, "Máximo 200 caracteres").optional().nullable(),
  direccion_numero: z.string().max(20, "Máximo 20 caracteres").optional().nullable(),
  codigo_postal: z.string()
    .max(5, "Máximo 5 dígitos")
    .optional()
    .nullable()
    .refine((val) => !val || /^\d{5}$/.test(val), {
      message: "Código postal debe tener 5 dígitos"
    }),
  ciudad: z.string().max(100, "Máximo 100 caracteres").optional().nullable(),
  provincia: z.string().max(100, "Máximo 100 caracteres").optional().nullable(),
  pais: z.string().max(100, "Máximo 100 caracteres").default("España"),
  telefono: z.string()
    .max(20, "Máximo 20 caracteres")
    .optional()
    .nullable()
    .refine((val) => !val || /^[+\d\s()-]{9,}$/.test(val), {
      message: "Formato de teléfono inválido"
    }),
  email: z.string()
    .email("Email inválido")
    .max(255, "Máximo 255 caracteres")
    .optional()
    .nullable(),
  email_facturacion: z.string()
    .email("Email inválido")
    .max(255, "Máximo 255 caracteres")
    .optional()
    .nullable()
    .or(z.literal("")),
  telefono_facturacion: z.string()
    .max(20, "Máximo 20 caracteres")
    .optional()
    .nullable()
    .refine((val) => !val || /^[+\d\s()-]{9,}$/.test(val), {
      message: "Formato de teléfono inválido"
    }),
  numero_dgp: z.string().max(50, "Máximo 50 caracteres").optional().nullable(),
  numero_reci: z.string().max(50, "Máximo 50 caracteres").optional().nullable(),
});

type EmpresaFormValues = z.infer<typeof empresaFormSchema>;

interface DocConfigState {
  color_primario: string;
  color_secundario: string;
  color_acento: string;
  mostrar_logo: boolean;
  texto_pie_pagina: string;
  texto_legal: string;
  mostrar_datos_empresa: boolean;
}

const DEFAULT_DOC_CONFIG: DocConfigState = {
  color_primario: '#333333',
  color_secundario: '#808080',
  color_acento: '#3B82F6',
  mostrar_logo: true,
  texto_pie_pagina: '',
  texto_legal: '',
  mostrar_datos_empresa: true,
};

export default function ConfiguracionEmpresa() {
  const { empresa, perfil, hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [selloUrl, setSelloUrl] = useState<string | null>(null);

  // Document config state
  const [docConfig, setDocConfig] = useState<DocConfigState>(DEFAULT_DOC_CONFIG);
  const [docConfigLoading, setDocConfigLoading] = useState(false);
  const [docConfigId, setDocConfigId] = useState<string | null>(null);

  // Modo cómputo de ausencias
  const [modoAusencias, setModoAusencias] = useState<"laborables" | "naturales">("laborables");
  const [ausenciasLoading, setAusenciasLoading] = useState(false);

  const form = useForm<EmpresaFormValues>({
    resolver: zodResolver(empresaFormSchema),
    defaultValues: {
      nombre: "",
      razon_social: "",
      nif_cif: "",
      direccion_calle: "",
      direccion_numero: "",
      codigo_postal: "",
      ciudad: "",
      provincia: "",
      pais: "España",
      telefono: "",
      email: "",
      email_facturacion: "",
      telefono_facturacion: "",
      numero_dgp: "",
      numero_reci: "",
    },
  });

  useEffect(() => {
    if (!hasRole("admin")) {
      navigate("/");
      return;
    }

    if (empresa) {
      form.reset({
        nombre: empresa.nombre || "",
        razon_social: empresa.razon_social || "",
        nif_cif: empresa.nif_cif || "",
        direccion_calle: (empresa as any).direccion_calle || "",
        direccion_numero: (empresa as any).direccion_numero || "",
        codigo_postal: (empresa as any).codigo_postal || "",
        ciudad: (empresa as any).ciudad || "",
        provincia: (empresa as any).provincia || "",
        pais: (empresa as any).pais || "España",
        telefono: empresa.telefono || "",
        email: empresa.email || "",
        email_facturacion: (empresa as any).email_facturacion || "",
        telefono_facturacion: (empresa as any).telefono_facturacion || "",
        numero_dgp: (empresa as any).numero_dgp || "",
        numero_reci: (empresa as any).numero_reci || "",
      });
      setLogoUrl(getImageUrl(empresa.logo_url) || null);
      setSelloUrl(getImageUrl((empresa as any).sello_url) || null);
      loadDocConfig(empresa.id);
      setModoAusencias(((empresa as any).modo_computo_ausencias === "naturales") ? "naturales" : "laborables");
    }
  }, [empresa, hasRole, navigate, form]);

  const saveModoAusencias = async () => {
    if (!empresa?.id) return;
    setAusenciasLoading(true);
    try {
      const { error } = await supabase
        .from("empresas")
        .update({ modo_computo_ausencias: modoAusencias } as any)
        .eq("id", empresa.id);
      if (error) throw error;
      toast.success("Modo de cómputo guardado");
    } catch (e: any) {
      toast.error(e.message || "Error al guardar");
    } finally {
      setAusenciasLoading(false);
    }
  };

  const loadDocConfig = async (empresaId: string) => {
    const { data } = await supabase
      .from('documento_config' as any)
      .select('*')
      .eq('empresa_id', empresaId)
      .maybeSingle();

    if (data) {
      setDocConfigId((data as any).id);
      setDocConfig({
        color_primario: (data as any).color_primario || DEFAULT_DOC_CONFIG.color_primario,
        color_secundario: (data as any).color_secundario || DEFAULT_DOC_CONFIG.color_secundario,
        color_acento: (data as any).color_acento || DEFAULT_DOC_CONFIG.color_acento,
        mostrar_logo: (data as any).mostrar_logo ?? true,
        texto_pie_pagina: (data as any).texto_pie_pagina || '',
        texto_legal: (data as any).texto_legal || '',
        mostrar_datos_empresa: (data as any).mostrar_datos_empresa ?? true,
      });
    }
  };

  const saveDocConfig = async () => {
    if (!empresa?.id) return;
    setDocConfigLoading(true);

    try {
      const payload = {
        empresa_id: empresa.id,
        color_primario: docConfig.color_primario,
        color_secundario: docConfig.color_secundario,
        color_acento: docConfig.color_acento,
        mostrar_logo: docConfig.mostrar_logo,
        texto_pie_pagina: docConfig.texto_pie_pagina || null,
        texto_legal: docConfig.texto_legal || null,
        mostrar_datos_empresa: docConfig.mostrar_datos_empresa,
      };

      if (docConfigId) {
        const { error } = await supabase
          .from('documento_config' as any)
          .update(payload as any)
          .eq('id', docConfigId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('documento_config' as any)
          .insert(payload as any)
          .select('id')
          .single();
        if (error) throw error;
        setDocConfigId((data as any).id);
      }

      clearBrandingCache(empresa.id);
      toast.success("Configuración de documentos guardada");
    } catch (error) {
      console.error("Error saving doc config:", error);
      toast.error("Error al guardar la configuración de documentos");
    } finally {
      setDocConfigLoading(false);
    }
  };

  const generatePreviewPdf = async () => {
    if (!empresa) return;

    const empresaInfo: EmpresaInfo = {
      nombre: empresa.nombre,
      razon_social: empresa.razon_social,
      nif_cif: empresa.nif_cif,
      telefono: empresa.telefono,
      email: empresa.email,
      logo_url: empresa.logo_url,
      direccion_calle: (empresa as any).direccion_calle,
      direccion_numero: (empresa as any).direccion_numero,
      codigo_postal: (empresa as any).codigo_postal,
      ciudad: (empresa as any).ciudad,
      provincia: (empresa as any).provincia,
    };

    const builder = await PdfDocumentBuilder.create(empresaInfo, {
      colorPrimario: docConfig.color_primario,
      colorSecundario: docConfig.color_secundario,
      colorAcento: docConfig.color_acento,
      mostrarLogo: docConfig.mostrar_logo,
      mostrarDatosEmpresa: docConfig.mostrar_datos_empresa,
      textoPiePagina: docConfig.texto_pie_pagina || undefined,
      textoLegal: docConfig.texto_legal || undefined,
    });

    builder.addHeader('Documento de Ejemplo', 'Vista previa de personalización');
    builder.addEmpresaDetails();
    builder.addSpacer(5);

    builder.addSection('Datos del Cliente');
    builder.addFieldsGrid([
      { label: 'Cliente', value: 'Ejemplo S.L.' },
      { label: 'NIF/CIF', value: 'B12345678' },
      { label: 'Dirección', value: 'Calle Principal 1' },
      { label: 'Ciudad', value: '28001 Madrid' },
    ], 2);

    builder.addSection('Información del Trabajo');
    builder.addFieldsGrid([
      { label: 'Fecha', value: new Date().toLocaleDateString('es-ES') },
      { label: 'Técnico', value: perfil?.nombre || 'Técnico' },
      { label: 'Estado', value: 'Finalizado' },
      { label: 'Duración', value: '2h 30m' },
    ], 2);

    builder.addSection('Descripción');
    builder.addText('Este es un documento de ejemplo para previsualizar cómo se verán sus documentos con la configuración actual de colores y branding.', 10);

    builder.addTable(
      ['Elemento', 'Estado', 'Observaciones'],
      [
        ['Central de alarma', 'OK', 'Funcionamiento correcto'],
        ['Detector de humo', 'OK', 'Sin incidencias'],
        ['Sirena exterior', 'Anomalía', 'Requiere revisión'],
      ],
    );

    if (docConfig.texto_legal) {
      builder.addLegalText(docConfig.texto_legal);
    }

    builder.addSignatureBlock([
      { label: 'Firma del Técnico', nombre: perfil?.nombre || 'Técnico' },
      { label: 'Sello de la empresa', esEmpresa: true },
      { label: 'Firma del Cliente', nombre: 'Cliente Ejemplo' },
    ]);

    builder.save(`vista_previa_documentos_${empresa.nombre.replace(/\s+/g, '_')}.pdf`);
  };

  const onSubmit = async (values: EmpresaFormValues) => {
    if (!empresa?.id) {
      toast.error("No se pudo identificar la empresa");
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from("empresas")
        .update({
          nombre: values.nombre,
          razon_social: values.razon_social || null,
          nif_cif: values.nif_cif || null,
          direccion_calle: values.direccion_calle || null,
          direccion_numero: values.direccion_numero || null,
          codigo_postal: values.codigo_postal || null,
          ciudad: values.ciudad || null,
          provincia: values.provincia || null,
          pais: values.pais,
          telefono: values.telefono || null,
          email: values.email || null,
          email_facturacion: values.email_facturacion || null,
          telefono_facturacion: values.telefono_facturacion || null,
          numero_dgp: values.numero_dgp || null,
          numero_reci: values.numero_reci || null,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", empresa.id);

      if (error) throw error;

      toast.success("Configuración actualizada correctamente");
      window.location.reload();
    } catch (error) {
      console.error("Error al actualizar empresa:", error);
      toast.error("Error al actualizar la configuración");
    } finally {
      setLoading(false);
    }
  };

  if (!empresa || !hasRole("admin")) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configuración de la Empresa</h1>
        <p className="text-muted-foreground mt-2">
          Gestiona la información de tu empresa para facturación y documentos oficiales
        </p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" className="gap-2">
            <Building2 className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="documentos" className="gap-2">
            <Palette className="h-4 w-4" />
            Documentos
          </TabsTrigger>
          <TabsTrigger value="plantillas" className="gap-2">
            <FolderOpen className="h-4 w-4" />
            Plantillas de entrega
          </TabsTrigger>
          <TabsTrigger value="contratos" className="gap-2">
            <FileSignature className="h-4 w-4" />
            Contratos
          </TabsTrigger>
          <TabsTrigger value="certificados" className="gap-2">
            <FileSignature className="h-4 w-4" />
            Certificados
          </TabsTrigger>
          <TabsTrigger value="ausencias" className="gap-2">
            <CalendarDays className="h-4 w-4" />
            Ausencias
          </TabsTrigger>
          <TabsTrigger value="integraciones" className="gap-2">
            <Plug className="h-4 w-4" />
            Integraciones
          </TabsTrigger>
        </TabsList>

        {/* TAB: General */}
        <TabsContent value="general">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Información General */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Información General
                  </CardTitle>
                  <CardDescription>
                    Nombre comercial y datos básicos de la empresa
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="nombre"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre Comercial *</FormLabel>
                        <FormControl>
                          <Input placeholder="Mi Empresa S.L." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="razon_social"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Razón Social</FormLabel>
                        <FormControl>
                          <Input placeholder="Nombre completo para facturación" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormDescription>Nombre oficial de la empresa para documentos legales</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Separator />
                  <div>
                    <LogoUpload
                      empresaId={empresa.id}
                      currentLogoUrl={logoUrl}
                      onLogoUpdated={setLogoUrl}
                    />
                  </div>
                  <Separator />
                  <div>
                    <SelloUpload
                      empresaId={empresa.id}
                      currentSelloUrl={selloUrl}
                      onSelloUpdated={setSelloUrl}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Datos Fiscales */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Datos Fiscales
                  </CardTitle>
                  <CardDescription>Información fiscal y dirección para facturación</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="nif_cif"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>NIF/CIF</FormLabel>
                        <FormControl>
                          <Input placeholder="B12345678" {...field} value={field.value || ""} className="uppercase" />
                        </FormControl>
                        <FormDescription>Número de identificación fiscal de la empresa</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form.control} name="numero_dgp" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nº DGP</FormLabel>
                        <FormControl><Input placeholder="Nº inscripción DGP (Seguridad)" {...field} value={field.value || ""} /></FormControl>
                        <FormDescription>Aparecerá en contratos e informes de seguridad</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="numero_reci" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nº RECI</FormLabel>
                        <FormControl><Input placeholder="Nº RECI (PCI / contraincendios)" {...field} value={field.value || ""} /></FormControl>
                        <FormDescription>Aparecerá en contratos e informes de PCI</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <Separator className="my-4" />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField control={form.control} name="direccion_calle" render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Calle</FormLabel>
                        <FormControl><Input placeholder="Calle Principal" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="direccion_numero" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número</FormLabel>
                        <FormControl><Input placeholder="123" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField control={form.control} name="codigo_postal" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Código Postal</FormLabel>
                        <FormControl><Input placeholder="28001" {...field} value={field.value || ""} maxLength={5} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="ciudad" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ciudad</FormLabel>
                        <FormControl><Input placeholder="Madrid" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="provincia" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Provincia</FormLabel>
                        <FormControl><Input placeholder="Madrid" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="pais" render={({ field }) => (
                    <FormItem>
                      <FormLabel>País</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </CardContent>
              </Card>

              {/* Datos de Contacto */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Datos de Contacto
                  </CardTitle>
                  <CardDescription>Información de contacto principal y de facturación</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form.control} name="telefono" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono Principal</FormLabel>
                        <FormControl><Input type="tel" placeholder="+34 912 345 678" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="email" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Principal</FormLabel>
                        <FormControl><Input type="email" placeholder="contacto@empresa.com" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Contacto de Facturación (Opcional)</h4>
                    <p className="text-sm text-muted-foreground">Si es diferente del contacto principal</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form.control} name="telefono_facturacion" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono Facturación</FormLabel>
                        <FormControl><Input type="tel" placeholder="+34 912 345 678" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="email_facturacion" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Facturación</FormLabel>
                        <FormControl><Input type="email" placeholder="facturacion@empresa.com" {...field} value={field.value || ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </CardContent>
              </Card>

              {/* Suscripción */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Información de Suscripción
                  </CardTitle>
                  <CardDescription>Datos de tu plan actual (solo lectura)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Plan Actual</label>
                      <div className="mt-2">
                        <Badge variant="outline" className="text-base">{empresa.plan_suscripcion || "Sin plan"}</Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Estado</label>
                      <div className="mt-2">
                        <Badge variant={empresa.estado === "activa" ? "default" : "destructive"} className="text-base">
                          {empresa.estado === "activa" ? "Activa" : empresa.estado === "suspendida" ? "Suspendida" : "Cancelada"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Fecha Inicio</label>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {empresa.fecha_inicio_suscripcion ? new Date(empresa.fecha_inicio_suscripcion).toLocaleDateString("es-ES") : "No disponible"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Fecha Fin</label>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {empresa.fecha_fin_suscripcion ? new Date(empresa.fecha_fin_suscripcion).toLocaleDateString("es-ES") : "No especificada"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Límite de Usuarios</label>
                      <p className="mt-2 text-sm text-muted-foreground">{empresa.limite_usuarios || "Sin límite"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Límite de Clientes</label>
                      <p className="mt-2 text-sm text-muted-foreground">{empresa.limite_clientes || "Sin límite"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => navigate("/")} disabled={loading}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" />Guardar Cambios</>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </TabsContent>

        {/* TAB: Documentos */}
        <TabsContent value="documentos">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Personalización de Documentos PDF
                </CardTitle>
                <CardDescription>
                  Configura los colores, logotipo y textos que aparecerán en todos los documentos generados (partes de trabajo, informes, mandatos SEPA)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Colors */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Colores Corporativos</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="color_primario">Color Primario</Label>
                      <div className="flex items-center gap-2">
                        <input
                          id="color_primario"
                          type="color"
                          value={docConfig.color_primario}
                          onChange={e => setDocConfig(prev => ({ ...prev, color_primario: e.target.value }))}
                          className="h-10 w-14 rounded border cursor-pointer"
                        />
                        <Input
                          value={docConfig.color_primario}
                          onChange={e => setDocConfig(prev => ({ ...prev, color_primario: e.target.value }))}
                          className="flex-1 font-mono text-sm"
                          maxLength={7}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Títulos y texto principal</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="color_secundario">Color Secundario</Label>
                      <div className="flex items-center gap-2">
                        <input
                          id="color_secundario"
                          type="color"
                          value={docConfig.color_secundario}
                          onChange={e => setDocConfig(prev => ({ ...prev, color_secundario: e.target.value }))}
                          className="h-10 w-14 rounded border cursor-pointer"
                        />
                        <Input
                          value={docConfig.color_secundario}
                          onChange={e => setDocConfig(prev => ({ ...prev, color_secundario: e.target.value }))}
                          className="flex-1 font-mono text-sm"
                          maxLength={7}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Etiquetas y texto secundario</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="color_acento">Color de Acento</Label>
                      <div className="flex items-center gap-2">
                        <input
                          id="color_acento"
                          type="color"
                          value={docConfig.color_acento}
                          onChange={e => setDocConfig(prev => ({ ...prev, color_acento: e.target.value }))}
                          className="h-10 w-14 rounded border cursor-pointer"
                        />
                        <Input
                          value={docConfig.color_acento}
                          onChange={e => setDocConfig(prev => ({ ...prev, color_acento: e.target.value }))}
                          className="flex-1 font-mono text-sm"
                          maxLength={7}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Líneas, cabeceras de tabla y destacados</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Toggles */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Opciones de Visualización</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mostrar logo en documentos</Label>
                      <p className="text-xs text-muted-foreground">El logo de la empresa aparecerá en la cabecera de cada PDF</p>
                    </div>
                    <Switch
                      checked={docConfig.mostrar_logo}
                      onCheckedChange={checked => setDocConfig(prev => ({ ...prev, mostrar_logo: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mostrar datos de empresa</Label>
                      <p className="text-xs text-muted-foreground">NIF, dirección y contacto aparecerán en el pie de página</p>
                    </div>
                    <Switch
                      checked={docConfig.mostrar_datos_empresa}
                      onCheckedChange={checked => setDocConfig(prev => ({ ...prev, mostrar_datos_empresa: checked }))}
                    />
                  </div>
                </div>

                <Separator />

                {/* Texts */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Textos Personalizados</h4>
                  <div className="space-y-2">
                    <Label htmlFor="pie_pagina">Pie de Página</Label>
                    <Textarea
                      id="pie_pagina"
                      placeholder="Texto personalizado para el pie de página (deje vacío para usar los datos de la empresa)"
                      value={docConfig.texto_pie_pagina}
                      onChange={e => setDocConfig(prev => ({ ...prev, texto_pie_pagina: e.target.value }))}
                      className="h-20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="texto_legal">Cláusula Legal</Label>
                    <Textarea
                      id="texto_legal"
                      placeholder="Texto legal que aparecerá en documentos como mandatos SEPA y contratos..."
                      value={docConfig.texto_legal}
                      onChange={e => setDocConfig(prev => ({ ...prev, texto_legal: e.target.value }))}
                      className="h-24"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview bar */}
            <div className="flex items-center gap-3">
              {/* Color preview */}
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm text-muted-foreground">Vista previa de colores:</span>
                <div className="flex gap-1">
                  <div className="h-6 w-6 rounded" style={{ backgroundColor: docConfig.color_primario }} title="Primario" />
                  <div className="h-6 w-6 rounded" style={{ backgroundColor: docConfig.color_secundario }} title="Secundario" />
                  <div className="h-6 w-6 rounded" style={{ backgroundColor: docConfig.color_acento }} title="Acento" />
                </div>
              </div>

              <Button variant="outline" onClick={generatePreviewPdf} className="gap-2">
                <Download className="h-4 w-4" />
                Vista Previa PDF
              </Button>

              <Button onClick={saveDocConfig} disabled={docConfigLoading}>
                {docConfigLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>
                ) : (
                  <><Save className="mr-2 h-4 w-4" />Guardar Documentos</>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="plantillas">
          <PlantillasDocumentosManager />
        </TabsContent>

        <TabsContent value="contratos">
          <PlantillasContratosManager />
        </TabsContent>

        <TabsContent value="certificados">
          <PlantillasCertificadosManager />
        </TabsContent>


        <TabsContent value="ausencias">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Cómputo de Días de Ausencia
              </CardTitle>
              <CardDescription>
                Define cómo se cuentan los días al solicitar vacaciones u otras ausencias.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2 max-w-md">
                <Label>Modo de cómputo</Label>
                <Select value={modoAusencias} onValueChange={(v) => setModoAusencias(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laborables">Días Laborables (lunes a viernes)</SelectItem>
                    <SelectItem value="naturales">Días Naturales (todos los días)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {modoAusencias === "naturales"
                    ? "Cupo por defecto sugerido: 30 días naturales/año."
                    : "Cupo por defecto sugerido: 22 días laborables/año."}
                </p>
                <p className="text-xs text-muted-foreground">
                  Solo afecta a las ausencias creadas a partir de ahora.
                </p>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveModoAusencias} disabled={ausenciasLoading}>
                  {ausenciasLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" />Guardar</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integraciones" className="space-y-4">
          <HoldedIntegracionSection />
          <HoldedAutomatizacionSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
